import React, { useImperativeHandle, useRef, useState } from 'react';

import type {
  HandleUploadFail,
  HandleUploadProgress,
  HandleUploadSuccess,
  MediaUpload,
  MediaUploaderProps,
} from './MediaUploader.typed';
import * as Styled from './MediaUploader.styled';
import { Preview } from './components/Preview/Preview';
import Empty from './components/Empty/Empty';
import { createMedia } from 'domains';
import { MediaUploaderElement } from './MediaUploader.typed';
import { logger } from '@dist-marketplace-frontend/services';

// TODO: Create a hook for logic
export const MediaUploader = React.forwardRef<
  MediaUploaderElement,
  MediaUploaderProps
>((props, ref) => {
  const {
    id,
    media,
    onUpload,
    maxUploads,
    acceptedFileTypes,
    renderThumbnail,
    emptyText,
    onSort,
    compress = true,
    batchSize = 5,
  } = props;

  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  // Ref required for asnyc callbacks
  const mediaRef = useRef(media);
  const setMedia = (data: MediaUpload[]) => {
    mediaRef.current = data;
    props.setMedia(data);
  };
  const inputRef = useRef<HTMLInputElement | null>(null);
  const emptyRef = useRef<HTMLDivElement | null>(null);
  const addButtonRef = useRef<HTMLButtonElement | null>(null);

  // When the parent component calls focus on the ref this will be called
  useImperativeHandle(
    ref,
    () => {
      return {
        focus() {
          if (media.length) {
            addButtonRef.current && addButtonRef.current.focus();
          }
          emptyRef.current && emptyRef.current.focus();
        },
      };
    },
    [emptyRef, media.length, addButtonRef],
  );

  const isEmpty = media.length === 0;

  const browse = () => {
    inputRef?.current?.click();
  };
  const onBrowse = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Upload files
    const { files } = event.target;
    if (!files) return;
    await handleFiles(files);
  };
  const updateMedia = (
    id: string,
    callback: (item: MediaUpload) => MediaUpload,
  ) => {
    const newUploads = [...mediaRef.current];
    const index = newUploads.findIndex((upload) => upload.id === id);
    if (index === -1) return;
    const updatedMedia = callback(newUploads[index]);
    newUploads[index] = updatedMedia;
    setMedia(newUploads);
  };
  const createNewMedia = (): MediaUpload => {
    return createMedia({ state: 'uploading', url: null });
  };

  const handleUpload = async (file: File, id: string) => {
    const formData = new FormData();
    formData.append('file', file);
    await onUpload({
      data: formData,
      onSuccess: (data) => handleUploadSuccess(data, id),
      onError: (message) => handleUploadError(message, id),
      onProgress: (progress) => handleUploadProgress(progress, id),
    });
  };
  const handleUploadProgress: HandleUploadProgress = (progress, fileId) => {
    // Don't update state if progress is 100%
    // It may conflict with onUploadSuccess
    if (progress < 100) {
      updateMedia(fileId, (item) => {
        item.progress = progress;
        return item;
      });
    }
  };
  const handleUploadSuccess: HandleUploadSuccess = (data, fileId) => {
    const index = mediaRef.current.findIndex((upload) => upload.id === fileId);
    // Delete the preview URL to avoid memory leaks
    if (index >= 0) {
      const url = mediaRef.current[index].url;
      if (url) {
        URL.revokeObjectURL(url);
      }
    }
    updateMedia(fileId, () => {
      return data;
    });
  };
  const handleUploadError: HandleUploadFail = (message, fileId) => {
    updateMedia(fileId, (item) => {
      item.state = 'error';
      item.errorMessage = message;
      return item;
    });
  };

  const discardFile = async (id: string) => {
    const index = media.findIndex((item) => item.id === id);
    if (index < 0) return;
    const url = media[index].url;
    if (url) {
      // When discarding a file that failed to upload, remove the object URL to avoid memory leaks
      URL.revokeObjectURL(url);
    }
    const newMediaUploads = media.filter((item) => item.id !== id);
    setMedia(newMediaUploads);
  };
  const validateFile = (file: File): string | null => {
    let error = null;
    if (!acceptedFileTypes.includes(file.type)) {
      error = 'Invalid file type';
    }
    return error;
  };
  const loadImage = (path: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.src = path;
      img.onload = () => {
        resolve(img);
      };
      img.onerror = (error) => {
        reject(error);
      };
    });
  };
  const canvasToBlob = async (
    canvas: HTMLCanvasElement,
    type = 'image/png',
    quality: number,
  ): Promise<Blob | null> => {
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        type,
        quality,
      );
    });
  };
  const compressFile = async (file: File): Promise<File> => {
    const imagePath = URL.createObjectURL(file);
    const image = await loadImage(imagePath);

    URL.revokeObjectURL(image.src);
    const canvas = document.createElement('canvas');
    let scaledWidth = image.width;
    let scaledHeight = image.height;
    const maxWidth = 1600;
    const maxHeight = 1600;
    const quality = 1;

    if (scaledWidth > maxWidth) {
      const scaleFactor = maxWidth / scaledWidth;
      scaledWidth *= scaleFactor;
      scaledHeight *= scaleFactor;
    }

    if (scaledHeight > maxHeight) {
      const scaleFactor = maxHeight / scaledHeight;
      scaledWidth *= scaleFactor;
      scaledHeight *= scaleFactor;
    }

    // Set the canvas size to the scaled dimensions
    canvas.width = scaledWidth;
    canvas.height = scaledHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas 2D context is not supported');
    }
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, scaledWidth, scaledHeight);

    const blob = await canvasToBlob(canvas, file.type, quality);
    if (blob) {
      return new File([blob], file.name, {
        type: file.type,
        lastModified: new Date().getTime(),
      });
    }
    throw new Error('failed to compress file');
  };
  const capFiles = (files: File[]): File[] => {
    if (media.length + files.length > maxUploads) {
      const filesToRemove = media.length + files.length - maxUploads;
      if (filesToRemove) {
        files.splice(-filesToRemove);
      }
    }
    return files;
  };
  const handleFiles = async (data: FileList) => {
    const filesArray = capFiles(Array.from(data));
    const newUploads: MediaUpload[] = [];
    const compressedFiles: (File | null)[] = [];

    // Process each file: validate -> compress -> create preview -> add to media
    for (const file of filesArray) {
      const error = validateFile(file);
      const mediaUpload = createNewMedia();

      if (error) {
        // Invalid file: create media with error state (no preview)
        mediaUpload.state = 'error';
        mediaUpload.errorMessage = error;
        newUploads.push(mediaUpload);
        compressedFiles.push(null);
      } else {
        // Valid file: compress first, then create preview
        try {
          let processedFile: File;
          const fileName = file.name;

          if (compress) {
            logger.info({ message: `Compressing media`, fileName });
            processedFile = await compressFile(file);
            logger.info({ message: `Successfully compressed media`, fileName });
          } else {
            processedFile = file;
          }

          // Create preview URL from compressed file
          mediaUpload.url = URL.createObjectURL(processedFile);
          newUploads.push(mediaUpload);
          compressedFiles.push(processedFile);
        } catch (compressionError) {
          logger.error({
            error: compressionError,
            message: `Failed to compress media`,
            fileName: file.name,
          });
          mediaUpload.state = 'error';
          mediaUpload.errorMessage = 'Failed to compress the image';
          newUploads.push(mediaUpload);
          compressedFiles.push(null);
        }
      }

      // Update state after each file is processed to show immediate feedback
      setMedia([...media, ...newUploads]);
    }

    // Now batch upload only the valid, compressed files
    const validUploadsWithFiles = newUploads
      .map((upload, index) => ({ upload, file: compressedFiles[index] }))
      .filter(
        ({ upload, file }) => upload.state === 'uploading' && file !== null,
      );

    for (let i = 0; i < validUploadsWithFiles.length; i += batchSize) {
      const batch = validUploadsWithFiles.slice(i, i + batchSize);
      const promises = batch.map(({ upload, file }) =>
        handleUpload(file as File, upload.id),
      );

      // Wait for the batch to complete before proceeding to next batch
      await Promise.all(promises);
    }
  };

  const onFileDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingFiles(false);
    await handleFiles(event.dataTransfer.files);
  };

  const onFileDrag = (event: React.DragEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setIsDraggingFiles(true);
  };

  return (
    <Styled.Wrapper
      isEmpty={isEmpty}
      isDragging={isDraggingFiles}
      onDrop={onFileDrop}
      onDragOver={onFileDrag}
      onDragLeave={() => setIsDraggingFiles(false)}
      onDragExit={() => setIsDraggingFiles(false)}
      onDragEnd={() => setIsDraggingFiles(false)}
    >
      <Styled.WrapperInner isDragging={isDraggingFiles}>
        <Styled.Input
          id={id}
          ref={inputRef}
          type="file"
          data-testid="media-file-input"
          multiple={true}
          onChangeCapture={onBrowse}
          accept={acceptedFileTypes.join(',')}
          disabled={media.length === maxUploads}
        />
        {isEmpty ? (
          <Empty
            ref={emptyRef}
            onPressEnter={browse}
            onClick={browse}
            text={emptyText}
          />
        ) : (
          <Preview
            addButtonRef={addButtonRef}
            setMedia={setMedia}
            media={media}
            removeMedia={discardFile}
            onClickAdd={browse}
            maxUploads={maxUploads}
            renderThumbnail={renderThumbnail}
            onSort={onSort}
          />
        )}
      </Styled.WrapperInner>
    </Styled.Wrapper>
  );
});

export default MediaUploader;
